import { useCallback, useEffect, useMemo, useState } from "react";
import { useUnit } from "effector-react";
import { $sectorsTerms } from "@/core/store/terms";
import { Term } from "@/core/store/terms/types";

// Tree node structure for hierarchical sectors
export interface SectorNode {
  id: string;
  label: string;
  children?: SectorNode[];
}

interface InterestsViewModelProps {
  selectedSectors?: number[];
  onSectorsChange?: (sectors: number[]) => void;
}

export const InterestsViewModel = (props?: InterestsViewModelProps) => {
  const sectorsTerms = useUnit($sectorsTerms);

  const [expandedSectors, setExpandedSectors] = useState<Set<string>>(
    new Set()
  );
  const [selectedSectors, setSelectedSectors] = useState<Set<string>>(
    new Set(props?.selectedSectors?.map((id) => id.toString()) || [])
  );
  const [searchQuery, setSearchQuery] = useState("");

  // Update selectedSectors when prop changes
  useEffect(() => {
    if (props?.selectedSectors) {
      setSelectedSectors(
        new Set(props.selectedSectors.map((id) => id.toString()))
      );
    }
  }, [props?.selectedSectors]);

  // Build sectors tree structure (same as OrganizationInfoTab)
  const sectorsTree: SectorNode[] = useMemo(() => {
    if (!sectorsTerms.data || sectorsTerms.data.length === 0) {
      return [];
    }

    const nodes: Record<string, SectorNode> = {};
    const tree: SectorNode[] = [];
    const childIds = new Set<string>(); // Track which nodes are children

    // First, create all nodes
    sectorsTerms.data.forEach((sector) => {
      nodes[sector.tid] = {
        id: sector.tid,
        label: sector.name,
        children: [],
      };
    });

    // Then, build the tree structure and track children
    sectorsTerms.data.forEach((sector) => {
      if (sector.parent && sector.parent !== "0" && nodes[sector.parent]) {
        // Has a parent - add to parent's children
        nodes[sector.parent].children?.push(nodes[sector.tid]);
        childIds.add(sector.tid); // Mark this as a child
      }
    });

    // Only add root nodes (those that are not children)
    sectorsTerms.data.forEach((sector) => {
      if (!childIds.has(sector.tid)) {
        tree.push(nodes[sector.tid]);
      }
    });

    return tree;
  }, [sectorsTerms.data]);

  const onSearchQueryChange = useCallback(
    (query: string) => setSearchQuery(query),
    []
  );

  const toggleSectorExpansion = useCallback((sectorId: string) => {
    setExpandedSectors((prev) => {
      const next = new Set(prev);
      if (next.has(sectorId)) next.delete(sectorId);
      else next.add(sectorId);
      return next;
    });
  }, []);

  const toggleSectorSelection = useCallback(
    (sectorId: string) => {
      const findNode = (
        nodes: SectorNode[],
        nodeId: string
      ): SectorNode | null => {
        for (const node of nodes) {
          if (node.id === nodeId) return node;
          if (node.children) {
            const found = findNode(node.children, nodeId);
            if (found) return found;
          }
        }
        return null;
      };

      const getDescendants = (node: SectorNode): string[] => {
        let ids: string[] = [];
        if (node.children) {
          for (const child of node.children) {
            ids.push(child.id);
            ids = ids.concat(getDescendants(child));
          }
        }
        return ids;
      };

      setSelectedSectors((prev) => {
        const currentSectors = new Set(prev);
        const node = findNode(sectorsTree, sectorId);
        const descendants = node ? getDescendants(node) : [];
        const allIds = [sectorId, ...descendants];

        if (currentSectors.has(sectorId)) {
          // Deselect this sector and all its children
          allIds.forEach((id) => currentSectors.delete(id));
        } else {
          // Select this sector and all its children
          allIds.forEach((id) => currentSectors.add(id));
        }

        // Notify parent component of sector changes (convert to numbers)
        const sectorsArray = Array.from(currentSectors).map((id) =>
          parseInt(id)
        );
        props?.onSectorsChange?.(sectorsArray);

        return currentSectors;
      });
    },
    [sectorsTree, props]
  );

  const filteredSectorsTree = useMemo(() => {
    if (!searchQuery) return sectorsTree;

    const filterTree = (nodes: SectorNode[]): SectorNode[] => {
      return nodes.reduce((acc, node) => {
        const matchesSearch = node.label
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

        const filteredChildren = node.children
          ? filterTree(node.children)
          : [];

        if (matchesSearch || filteredChildren.length > 0) {
          acc.push({
            ...node,
            children:
              filteredChildren.length > 0 ? filteredChildren : node.children,
          });
        }
        return acc;
      }, [] as SectorNode[]);
    };

    return filterTree(sectorsTree);
  }, [searchQuery, sectorsTree]);

  // Get selected sector names for chips display
  const selectedSectorNames = useMemo(() => {
    return Array.from(selectedSectors)
      .map((tid) => {
        const sector = sectorsTerms.data.find((s: Term) => s.tid === tid);
        return sector ? { id: tid, name: sector.name } : null;
      })
      .filter((s): s is { id: string; name: string } => s !== null);
  }, [selectedSectors, sectorsTerms.data]);

  const removeSector = useCallback(
    (sectorId: string) => {
      toggleSectorSelection(sectorId);
    },
    [toggleSectorSelection]
  );

  return {
    searchQuery,
    onSearchQueryChange,
    expandedSectors,
    selectedSectors,
    selectedSectorNames,
    filteredSectorsTree,
    sectorsTree,
    sectorsTerms: sectorsTerms.data,
    toggleSectorExpansion,
    toggleSectorSelection,
    removeSector,
  };
};
